import { Button } from '@wordpress/components';
import { useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import { Cropper, CircleStencil, RectangleStencil } from 'react-advanced-cropper';

import getDefaultCrop from './utils/getDefaultCrop';
import { transformCoordinatesIntoCrop, transformCropIntoCoordinates } from './utils/transforms';

/**
 * Wraps the 'react-advanced-cropper' library and adds some additional functionality:
 * - Provides a reset button to reset the crop to the default position.
 * - Ensures that crops are always within the bounds of the image and tied to the image's aspect ratio.
 *
 * @param {Object}                  props             Component props
 * @param {string}                  props.imageUrl    Original image URL
 * @param {number}                  props.aspectRatio Aspect ratio of the crop
 * @param {Crop}                    props.value       The crop value, used to set the initial cropper state
 * @param {string}                  props.label       Label for the cropper
 * @param {boolean}                 props.isCircular  Whether the crop should be circular
 * @param {(newCrop: Crop) => void} props.onChange    Callback function for when the crop changes
 *
 * @return {JSX.Element}                               ImageCropper component
 */
const ImageCropper = ({ imageUrl, aspectRatio, value, label, isCircular, onChange }) => {
  const cropperRef = useRef(null);

  /**
   * Gets the default crop coordinates based on the aspect ratio of the source image
   *
   * @param {Object}                          initialState           Cropper component initial state
   * @param {{width: number, height: number}} initialState.imageSize Image size
   *
   * @return {object | undefined}                                     Default coordinates
   */
  const getDefaultCoordinates = ({ imageSize }) =>
    transformCropIntoCoordinates(value, imageSize.width, imageSize.height);

  /**
   * Resets the crop to the original image size and position based on the aspect ratio
   */
  const handleReset = () => {
    const cropper = cropperRef?.current;
    if (!cropper) {
      return;
    }

    const image = cropper.getImage();
    if (!image) {
      return;
    }

    const defaultCrop = getDefaultCrop(image.width, image.height, aspectRatio);
    const defaultCoords = transformCropIntoCoordinates(defaultCrop, image.width, image.height);
    if (!defaultCoords) {
      return;
    }

    cropper.setCoordinates(defaultCoords);
  };

  /**
   * Transforms the crop coordinates into a Crop object and calls the onChange callback
   *
   * @param {*} cropper The cropper instance
   */
  const handleChange = (cropper) => {
    const coords = cropper.getCoordinates();
    const image = cropper.getImage();
    const newValue = transformCoordinatesIntoCrop(coords, image.width, image.height);
    onChange(newValue);
  };

  return (
    <div>
      {label && <h3>{label}</h3>}
      <Cropper
        className="mediapress-image-variation-picker-cropper"
        ref={cropperRef}
        src={imageUrl}
        stencilComponent={isCircular ? CircleStencil : RectangleStencil}
        defaultCoordinates={getDefaultCoordinates}
        minWidth={50}
        minHeight={50}
        onChange={handleChange}
        stencilProps={{
          aspectRatio: aspectRatio === -1 ? undefined : aspectRatio,
        }}
      />
      <footer>
        <Button variant="link" isDestructive onClick={handleReset}>
          {__('Revert to default', 'mediapress')}
        </Button>
      </footer>
    </div>
  );
};

export default ImageCropper;
